import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, LoadingIndicator, SimpleWizard, SimpleWizardStep, SimpleWizardConfirmStep } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { getOrderGroup, cancelOrderGroup, cancelOrder } from '@/services/orders';
import theme from './OrderCancelDialog.module.scss';
import Banner from '@/components/Banner';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OrderListItem from '../OrderListItem';
import { OrderGroup, ChangeRequest, BuyerOptionGroup, Order, OrderStatus, OptionGroupDto } from '@/types';
import { OrderCloseDialogCommonProps } from '.';
import { getChangeRequest } from '@/services/change-requests';
// import { searchBuyerOptionGroups } from '@/services/buyer-options';
import { notEmptyPredicate } from '@ygtechnologies/rubus-frontend-utilities';
// import BuyerOptionGroupList from '@/seller/components/buyer-options/BuyerOptionGroupList';
import { Radio } from '@/components/forms/Radio';
import { useUniqueId } from '@/utils/hooks';
import { searchOptionGroups } from '@/services/options/option-groups';

interface ChangeRequestOrderCancelDialogProps extends OrderCloseDialogCommonProps {
    order: Order;
}

enum Step {
    Intro,
    ReviewAllOrders,
    ReviewSingleOrder,
    ReviewOverrides,
    Success,
}

enum CancelMode {
    SingleOrder,
    AllOrders,
}

const ChangeRequestOrderCancelDialog: React.FC<ChangeRequestOrderCancelDialogProps> = ({
    show,
    onClose,
    onOrderCancel,
    order,
}) => {
    const [step, setStep] = useState(Step.Intro);
    const [orderGroup, setOrderGroup] = useState<OrderGroup | null>(null);
    const [changeRequest, setChangeRequest] = useState<ChangeRequest | null>(null);
    const [overriddenBuyerOptionGroups, setOverriddenBuyerOptionGroups] = useState<OptionGroupDto[] | null>(null);
    const [cancelMode, setCancelMode] = useState(CancelMode.AllOrders);

    const reset = () => {
        setOrderGroup(null);
        setChangeRequest(null);
        setStep(Step.Intro);
        setCancelMode(CancelMode.AllOrders);
    };

    const handleOpen = async () => {
        reset();

        const [orderGroup, changeRequest, buyerOptionGroups] = await Promise.all([
            getOrderGroup(order.orderGroupId),
            getChangeRequest(order.changeRequestId!),
            searchOptionGroups({ projectId: order.projectId, unitIds: [order.residentialUnitId] }),
        ]);

        const overriddenOptionGroupIds = changeRequest.optionGroupOverrides.map((x) => x.optionGroupId);

        setOrderGroup(orderGroup);
        setChangeRequest(changeRequest);
        setOverriddenBuyerOptionGroups(buyerOptionGroups.filter((x) => overriddenOptionGroupIds.includes(x.id)));
    };

    const handleSubmit = async () => {
        if (cancelMode === CancelMode.AllOrders) {
            await cancelOrderGroup(orderGroup!.id);

            triggerHandler(
                onOrderCancel,
                orderGroup!.orders.map((x) => x.id)
            );
        } else {
            await cancelOrder(order.id);

            triggerHandler(onOrderCancel, [order.id]);
        }
    };

    const handleFinish = () => {
        triggerHandler(onClose);
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const uniqueId = useUniqueId();

    const renderContent = () => {
        if (!orderGroup || !changeRequest || !overriddenBuyerOptionGroups) {
            return <LoadingIndicator />;
        }

        const canCancelSingleOrder = orderGroup.orders.some((x) => x.status !== OrderStatus.Canceled && x.id !== order.id);
        const showOverrideStep = overriddenBuyerOptionGroups.length > 0 && cancelMode === CancelMode.AllOrders;

        const steps = [
            Step.Intro,
            cancelMode === CancelMode.AllOrders ? Step.ReviewAllOrders : Step.ReviewSingleOrder,
            showOverrideStep ? Step.ReviewOverrides : null,
            Step.Success,
        ].filter(notEmptyPredicate);

        return (
            <SimpleWizard
                onCancel={step !== Step.Success ? handleCancelClick : undefined}
                steps={steps}
                currentStep={step}
                onStepChange={setStep}
                onSubmit={handleSubmit}
                onFinish={handleFinish}
                saveButtonProps={{ children: <Trans>Cancel order</Trans> }}>
                <SimpleWizardStep stepNumber={Step.Intro} header={<Trans>Choose cancel type</Trans>}>
                    {/* <Banner type="warning">
                        <Trans>
                            Please note this will cancel this individual order and all orders connected to this change
                            request. The associated change request will also be reopened.
                        </Trans>
                    </Banner> */}

                    <div className={theme.cancelMode}>
                        <Radio
                            labelClassName={theme.cancelModeRadio}
                            name={uniqueId}
                            checked={cancelMode === CancelMode.AllOrders}
                            onChange={(e) => setCancelMode(CancelMode.AllOrders)}>
                            <span className={theme.title}>
                                <Trans>Cancel all orders related to this change request</Trans>
                            </span>
                            <span className={theme.body}>
                                <Trans>
                                    The orders will be canceled and it will be possible to either close or reopen the change
                                    request.
                                </Trans>
                            </span>
                        </Radio>

                        <Radio
                            labelClassName={theme.cancelModeRadio}
                            name={uniqueId}
                            disabled={!canCancelSingleOrder}
                            checked={cancelMode === CancelMode.SingleOrder}
                            onChange={(e) => setCancelMode(CancelMode.SingleOrder)}>
                            <span className={theme.title}>
                                <Trans>Cancel this order only</Trans>
                            </span>
                            <span className={theme.body}>
                                <Trans>Only this order will be canceled, and the change request will stay as is.</Trans>
                            </span>
                        </Radio>
                    </div>
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.ReviewOverrides} header={<Trans>Option(s) overridden by order</Trans>}>
                    <Banner type="warning">
                        <Trans>
                            This change request overrides one or more options. If you continue, those overrides will be
                            undone.
                        </Trans>
                    </Banner>

                    {/* <BuyerOptionGroupList
                        dependencyErrors={[]}
                        canMakeChoices={false}
                        optionGroups={overriddenBuyerOptionGroups}
                    /> */}
                </SimpleWizardStep>

                <SimpleWizardStep
                    stepNumber={Step.ReviewAllOrders}
                    header={
                        <Trans>Confirm cancellation of orders related to change request #{orderGroup.changeRequestId}</Trans>
                    }>
                    <Banner type="warning">
                        <Trans>
                            <strong>Please note</strong> this will cancel this individual order and all orders connected to
                            change request{' '}
                            <strong>
                                #{changeRequest.id} ({changeRequest.subject})
                            </strong>
                            . The associated change request will also be reopened.
                        </Trans>
                    </Banner>

                    {orderGroup.orders
                        .filter((x) => x.status !== OrderStatus.Canceled)
                        .map((o) => (
                            <OrderListItem key={o.id} order={o} onChange={() => null} readOnly />
                        ))}
                </SimpleWizardStep>

                <SimpleWizardStep
                    stepNumber={Step.ReviewSingleOrder}
                    header={<Trans>Confirm cancellation of order #{order.orderNumber}</Trans>}>
                    <Banner type="warning">
                        <Trans>
                            <strong>Please note</strong> this operation will only cancel this individual order. The other
                            orders associated with this change request will not be changed, and the change request will not
                            be reopened.
                        </Trans>
                    </Banner>

                    <OrderListItem order={order} onChange={() => null} readOnly />
                </SimpleWizardStep>

                <SimpleWizardConfirmStep stepNumber={Step.Success} header={''}>
                    <div className={theme.confirmed}>
                        <FontAwesomeIcon height="124px" width="124px" icon={faCheckCircle} className={theme.icon} />
                        <br />

                        {cancelMode === CancelMode.AllOrders && (
                            <Trans>
                                {orderGroup.orders.length} orders cancelled and change request #{orderGroup.changeRequestId}{' '}
                                reopened successfully.
                            </Trans>
                        )}

                        {cancelMode === CancelMode.SingleOrder && (
                            <Trans>
                                Order #{order.orderNumber} is cancelled. The remaining orders in this change request remain
                                unchanged.
                            </Trans>
                        )}
                    </div>
                </SimpleWizardConfirmStep>
            </SimpleWizard>
        );
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} size="xl" onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Cancel order</Trans>
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
        </Modal>
    );
};
export default ChangeRequestOrderCancelDialog;
